import React from "react";
import { Link } from "react-router-dom";
import {
  Scale, CheckCircle2, AlertTriangle, Clock, RefreshCw, ExternalLink, Loader2, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, API } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "Aldrig";
  return new Date(iso).toLocaleDateString("da-DK", {
    day: "2-digit", month: "long", year: "numeric",
  });
}

function fmtDaysAgo(days) {
  if (days == null) return "—";
  if (days === 0) return "i dag";
  if (days === 1) return "1 dag siden";
  if (days < 30) return `${days} dage siden`;
  if (days < 365) return `${Math.round(days / 30)} mdr siden`;
  return `${Math.round(days / 365 * 10) / 10} år siden`;
}

export default function LegalReviewsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);
  const [notes, setNotes] = React.useState({});
  const [downloading, setDownloading] = React.useState(false);

  const downloadReport = async (months = 12) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("ema_admin_token");
      const res = await fetch(`${API}/admin/compliance-report.pdf?months=${months}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `EMA_Kurer_Compliance_Rapport_${today}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("Compliance-rapport hentet");
    } catch (e) {
      toast.error(`Kunne ikke hente rapport: ${e.message}`);
    } finally { setDownloading(false); }
  };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await adminApi().get("/admin/legal-reviews");
      setData(r);
    } catch (e) {
      toast.error("Kunne ikke hente lovside-status");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const markReviewed = async (key) => {
    setBusy(key);
    try {
      await adminApi().post(`/admin/legal-reviews/${key}/mark-reviewed`, {
        notes: notes[key] || "",
      });
      toast.success("Markeret som gennemgået — næste review om 12 mdr");
      setNotes((n) => ({ ...n, [key]: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke markere som gennemgået");
    } finally { setBusy(null); }
  };

  const markAllReviewed = async () => {
    const ok = window.confirm(
      "Bekræft: Markér ALLE 6 lovsider som gennemgået i dag?\n\n" +
      "Brug KUN denne knap når du har skimmet alle dokumenter igennem og bekræftet at de stadig er korrekte. " +
      "Det er din juridiske revisions-handling — Datatilsynet kan kræve dokumentation."
    );
    if (!ok) return;
    setBusy("__all__");
    try {
      const { data: r } = await adminApi().post("/admin/legal-reviews/mark-all-reviewed", {
        notes: "Bulk-gennemgået via admin-knap",
      });
      toast.success(`✓ ${r.marked} lovsider markeret som gennemgået`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke markere alle som gennemgået");
    } finally { setBusy(null); }
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="legal-loading">
        Indlæser lovside-status…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="legal-reviews-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Scale size={22} className="text-brand-red" />
            Lovsider — årlig revision
          </h2>
          <p className="text-xs text-brand-text2 mt-1 max-w-xl">
            Hold dine lovsider opdaterede mindst én gang om året. Markér en side som gennemgået
            efter du har læst den igennem og kontrolleret at den stadig matcher virkeligheden.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors flex-shrink-0"
          data-testid="legal-refresh"
        >
          <RefreshCw size={12} /> Opdater
        </button>
      </div>

      {/* Bulk mark all reviewed (annual quick action) */}
      <div className="bg-emerald-50 border border-emerald-300 p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="legal-bulk-section">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-emerald-900 flex items-center gap-2">
            <CheckCircle2 size={16} />
            Hurtig årsrevision
          </div>
          <p className="text-xs text-emerald-800 mt-1 max-w-2xl">
            Når du har gennemlæst <b>alle</b> 6 lovsider og bekræftet at de stadig matcher virkeligheden,
            kan du markere dem alle som gennemgået med ét klik. Bruges typisk én gang om året —
            handlingen logges i audit-sporet til Datatilsynet.
          </p>
        </div>
        <button
          onClick={markAllReviewed}
          disabled={busy === "__all__"}
          className="bg-emerald-700 text-white hover:bg-emerald-800 px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-60 flex-shrink-0"
          data-testid="legal-bulk-mark"
        >
          {busy === "__all__" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
          Markér ALLE som gennemgået
        </button>
      </div>

      {/* Compliance-rapport download */}
      <div className="bg-blue-50 border border-blue-300 p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="compliance-report-section">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base text-blue-900 flex items-center gap-2">
            <FileDown size={16} />
            Compliance-rapport (PDF)
          </div>
          <p className="text-xs text-blue-800 mt-1 max-w-xl">
            Komplet audit-rapport over alle compliance-aktiviteter de sidste 12 måneder — lovside-revisioner,
            mail-tests, Billy-fejl, email-fejl, chauffør-problemer, whistleblower-tal, backup-log og admin
            audit-spor. Brug til revisor eller myndighedstilsyn (Datatilsynet, SKAT, Digitaliseringsstyrelsen).
          </p>
        </div>
        <button
          onClick={() => downloadReport(12)}
          disabled={downloading}
          className="bg-blue-700 text-white hover:bg-blue-800 px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-60 flex-shrink-0"
          data-testid="compliance-download"
        >
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
          Hent PDF
        </button>
      </div>

      {/* Status banner */}
      {data.overdue_count > 0 ? (
        <div className="bg-red-50 border border-red-300 p-4 flex items-start gap-3" data-testid="legal-status-overdue">
          <AlertTriangle size={20} className="text-red-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-red-900">
              {data.overdue_count} {data.overdue_count === 1 ? "lovside er" : "lovsider er"} forældede ({">"}12 mdr siden review)
            </div>
            <div className="text-xs text-red-700/80 mt-1">
              Gennemgå dem nu — lovgivningen kan have ændret sig siden sidst.
            </div>
          </div>
        </div>
      ) : data.warning_count > 0 ? (
        <div className="bg-amber-50 border border-amber-300 p-4 flex items-start gap-3" data-testid="legal-status-warning">
          <Clock size={20} className="text-amber-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-amber-900">
              {data.warning_count} {data.warning_count === 1 ? "lovside skal" : "lovsider skal"} reviewes inden 30 dage
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-emerald-50 border border-emerald-300 p-4 flex items-start gap-3" data-testid="legal-status-ok">
          <CheckCircle2 size={20} className="text-emerald-700 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-emerald-900">Alle lovsider er opdaterede 🎯</div>
            <div className="text-xs text-emerald-700/80 mt-1">
              Næste planlagte revision: {fmtDate(data.items.map(i => i.next_due_at).filter(Boolean).sort()[0])}
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-brand divide-y divide-brand" data-testid="legal-list">
        {data.items.map((doc) => {
          const isOverdue = doc.is_overdue;
          const isWarning = doc.is_warning;
          return (
            <div key={doc.key} className="p-4" data-testid={`legal-doc-${doc.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {isOverdue && (
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-900 border border-red-300 font-bold uppercase flex items-center gap-1">
                        <AlertTriangle size={11} /> Forældet
                      </span>
                    )}
                    {isWarning && (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 font-bold uppercase flex items-center gap-1">
                        <Clock size={11} /> Snart due
                      </span>
                    )}
                    {!isOverdue && !isWarning && doc.last_reviewed_at && (
                      <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold uppercase flex items-center gap-1">
                        <CheckCircle2 size={11} /> OK
                      </span>
                    )}
                    <Link
                      to={doc.path}
                      target="_blank"
                      rel="noreferrer"
                      className="font-display font-bold text-base text-brand-dark hover:text-brand-red inline-flex items-center gap-1"
                      data-testid={`legal-open-${doc.key}`}
                    >
                      {doc.label} <ExternalLink size={12} />
                    </Link>
                  </div>
                  <div className="text-xs text-brand-text2 mt-1">
                    Sidste review: <b className="text-brand-dark">{fmtDate(doc.last_reviewed_at)}</b>
                    {doc.days_since_review != null && <> · {fmtDaysAgo(doc.days_since_review)}</>}
                    {doc.last_reviewed_by && <> · af {doc.last_reviewed_by}</>}
                  </div>
                  {doc.notes && (
                    <div className="mt-2 text-xs text-brand-text2 bg-brand-bg/50 border-l-2 border-brand-red px-2 py-1.5">
                      Note: {doc.notes}
                    </div>
                  )}
                  {doc.next_due_at && (
                    <div className="text-xs text-brand-text2 mt-1">
                      Næste review forfalder: <b>{fmtDate(doc.next_due_at)}</b>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0 min-w-[200px]">
                  <input
                    type="text"
                    value={notes[doc.key] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [doc.key]: e.target.value }))}
                    placeholder="Note (valgfri)"
                    className="border border-brand px-2 py-1.5 text-xs focus:outline-none focus:border-brand-dark"
                    data-testid={`legal-note-${doc.key}`}
                  />
                  <button
                    onClick={() => markReviewed(doc.key)}
                    disabled={busy !== null}
                    className="text-xs bg-emerald-700 text-white hover:bg-emerald-800 px-3 py-2 flex items-center justify-center gap-1 transition-colors disabled:opacity-60"
                    data-testid={`legal-mark-${doc.key}`}
                  >
                    {busy === doc.key ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                    Markér gennemgået
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-blue-50 border border-blue-300 p-3 text-xs text-blue-900">
        <b>Tip:</b> Læs siden igennem, tjek at info stadig stemmer (CVR, adresse, databehandlere, lovreferencer),
        og markér derefter som gennemgået. Notater gemmes som audit-spor i case af myndighedstilsyn.
      </div>
    </div>
  );
}
